import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { FaPhone, FaEnvelope, FaMapMarkerAlt, FaArrowLeft } from 'react-icons/fa';

const ContactCallout: React.FC<{ language: 'ar' | 'en' }> = ({ language }) => {
  const isRTL = language === 'ar';
  
  const contactInfo = [
    {
      icon: FaPhone,
      titleAr: 'اتصل بنا',
      titleEn: 'Call Us',
      value: '020224181844',
      link: 'tel:020224181844',
      color: 'from-green-500 to-green-600'
    },
    {
      icon: FaEnvelope,
      titleAr: 'راسلنا',
      titleEn: 'Email Us',
      value: 'info@meccaelbaraka.com',
      link: 'mailto:info@meccaelbaraka.com',
      color: 'from-blue-500 to-blue-600'
    },
    {
      icon: FaMapMarkerAlt,
      titleAr: 'موقعنا',
      titleEn: 'Our Location',
      value: '38 الملتقي العربي - شيراتون - مصر',
      link: '#',
      color: 'from-purple-500 to-purple-600'
    }
  ];

  return (
    <section className={`section-padding bg-gradient-to-br from-primary-900 via-primary-800 to-secondary-900 relative overflow-hidden ${isRTL ? 'font-cairo' : 'font-montserrat'}`} dir={isRTL ? 'rtl' : 'ltr'}>
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute inset-0" style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.1'%3E%3Ccircle cx='30' cy='30' r='2'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`
        }}></div>
      </div>

      <div className="container-custom relative z-10">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Content */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <motion.span
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              transition={{ delay: 0.2, duration: 0.5 }}
              className="inline-block px-4 py-2 bg-white/20 backdrop-blur-sm text-white rounded-full text-sm font-medium mb-4"
            >
              {language === 'ar' ? 'اتصل بنا' : 'Contact Us'}
            </motion.span>
            
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3, duration: 0.8 }}
              className="text-3xl md:text-4xl font-bold text-white mb-6"
            >
              {language === 'ar' ? 'دعنا نبدأ' : 'Let\'s Start'}
              <span className="text-secondary-400"> {language === 'ar' ? 'مشروعك معاً' : 'Your Project Together'}</span>
            </motion.h2>
            
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4, duration: 0.8 }}
              className="text-lg text-primary-100 mb-8 leading-relaxed"
            >
              {language === 'ar'
                ? 'فريقنا من الخبراء جاهز لمساعدتك في تخطيط وتنفيذ مشروعك بأفضل الطرق وأقل التكاليف. اتصل بنا الآن للحصول على استشارة مجانية وعرض سعر مخصص.'
                : 'Our team of experts is ready to help you plan and execute your project in the best ways and at the lowest costs. Contact us now for a free consultation and customized quote.'}
            </motion.p>

            {/* Contact Info */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5, duration: 0.8 }}
              className="space-y-4 mb-8"
            >
              {contactInfo.map((info, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.6 + index * 0.1, duration: 0.5 }}
                  className="flex items-center space-x-4 rtl:space-x-reverse"
                >
                  <div className={`w-12 h-12 bg-gradient-to-br ${info.color} rounded-lg flex items-center justify-center flex-shrink-0`}>
                    <info.icon className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-white mb-1">{isRTL ? info.titleAr : info.titleEn}</h3>
                    <a
                      href={info.link}
                      className="text-primary-100 hover:text-secondary-400 transition-colors"
                    >
                      {info.value}
                    </a>
                  </div>
                </motion.div>
              ))}
            </motion.div>

            {/* CTA Buttons */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.7, duration: 0.8 }}
              className="flex flex-col sm:flex-row gap-4"
            >
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="btn-secondary text-lg px-8 py-4"
              >
                {language === 'ar' ? 'ابدأ مشروعك الآن' : 'Start Your Project Now'}
              </motion.button>
              
              <Link
                to="/contact"
                className="btn-outline text-lg px-8 py-4 border-white text-white hover:bg-white hover:text-primary-600"
              >
                {language === 'ar' ? 'احصل على عرض سعر' : 'Get a Quote'}
              </Link>
            </motion.div>
          </motion.div>

          {/* Contact Form Preview */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 border border-white/20">
              <h3 className="text-2xl font-bold text-white mb-6 text-center">
                {language === 'ar' ? 'احصل على استشارة مجانية' : 'Get a Free Consultation'}
              </h3>
              
              <div className="space-y-4">
                <div className="bg-white/20 backdrop-blur-sm rounded-lg p-4">
                  <label className="block text-white text-sm font-medium mb-2">
                    {language === 'ar' ? 'الاسم الكامل' : 'Full Name'}
                  </label>
                  <input
                    type="text"
                    placeholder={language === 'ar' ? 'أدخل اسمك الكامل' : 'Enter your full name'}
                    className="w-full bg-white/20 border border-white/30 rounded-lg px-4 py-3 text-white placeholder-white/70 focus:outline-none focus:ring-2 focus:ring-secondary-400"
                  />
                </div>
                
                <div className="bg-white/20 backdrop-blur-sm rounded-lg p-4">
                  <label className="block text-white text-sm font-medium mb-2">
                    {language === 'ar' ? 'رقم الهاتف' : 'Phone Number'}
                  </label>
                  <input
                    type="tel"
                    placeholder={language === 'ar' ? 'أدخل رقم هاتفك' : 'Enter your phone number'}
                    className="w-full bg-white/20 border border-white/30 rounded-lg px-4 py-3 text-white placeholder-white/70 focus:outline-none focus:ring-2 focus:ring-secondary-400"
                  />
                </div>
                
                <div className="bg-white/20 backdrop-blur-sm rounded-lg p-4">
                  <label className="block text-white text-sm font-medium mb-2">
                    {language === 'ar' ? 'نوع المشروع' : 'Project Type'}
                  </label>
                  <select className="w-full bg-white/20 border border-white/30 rounded-lg px-4 py-3 text-white focus:outline-none focus:ring-2 focus:ring-secondary-400">
                    <option value="" className="text-gray-800">
                      {language === 'ar' ? 'اختر نوع المشروع' : 'Select project type'}
                    </option>
                    <option value="residential" className="text-gray-800">
                      {language === 'ar' ? 'سكني' : 'Residential'}
                    </option>
                    <option value="commercial" className="text-gray-800">
                      {language === 'ar' ? 'تجاري' : 'Commercial'}
                    </option>
                    <option value="industrial" className="text-gray-800">
                      {language === 'ar' ? 'صناعي' : 'Industrial'}
                    </option>
                    <option value="infrastructure" className="text-gray-800">
                      {language === 'ar' ? 'بنية تحتية' : 'Infrastructure'}
                    </option>
                  </select>
                </div>
                
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  className="w-full btn-secondary text-lg py-4"
                >
                  {language === 'ar' ? 'إرسال الطلب' : 'Send Request'}
                </motion.button>
              </div>
              
              <p className="text-primary-100 text-sm text-center mt-4">
                {language === 'ar' ? 'سنتواصل معك خلال 24 ساعة' : 'We will contact you within 24 hours'}
              </p>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default ContactCallout; 